import { ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getPhotos } from "../../queries/photosQueries";
import { getVesselInspectionData } from "../../queries/particularsQueries";
import { showRequestErrorToast } from "../../services/ErrorService";
import { Loading } from "../loading/Loading";
import { Galleria } from "primereact/galleria";
import React from "react";
import { AppContext } from "../../App";
import { AppContextType } from "../../types/AppContextType";
import { Button } from "primereact/button";
import { GetPhotosResponse } from "../../types/responses/GetPhotosResponse";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import "./Images.css";

export const Images = (): ReactElement => {
    const { t } = useTranslation("locale");
    const { inspectionId } = useParams();
    const { restrictedView } = React.useContext(AppContext) as AppContextType;
    const [selectedAlbum, setSelectedAlbum] = useState<GetPhotosResponse | undefined>();
    const [activeIndex, setActiveIndex] = useState(0);
    const toastId = useRef<null | string | number>(null);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    if (!inspectionId) {
        console.error("Images - No inspection id.");
        showRequestErrorToast("Images");
        return <div></div>;
    }

    const { data, isLoading, isError, error } = getPhotos(inspectionId, user);
    const { data: vesselData } = getVesselInspectionData(inspectionId, user);

    if (isLoading || !data) return <Loading />;

    if (isError) {
        console.error(error);
        showRequestErrorToast("Images");
        return <div></div>;
    }

    const albums = [...data.filter((album) => !album.isDefault)];

    if (albums.length === 0) {
        return (
            <div className="flex justify-content-center align-items-center p-2 images-no-content">
                {t("images.noImages")}
            </div>
        );
    }

    const handleDownloadAll = async () => {
        const zip = new JSZip();
        const totalFiles = data.filter((a) => !a.isDefault).flatMap((x) => x.images).length;
        let downloaded = 0;

        toastId.current = toast(t("documents.preparingFilesForDownload"), {
            type: "success",
            progress: 0,
            closeButton: false,
            closeOnClick: false,
        });

        try {
            for (const album of data.filter((a) => !a.isDefault)) {
                const folder = zip.folder(album.albumName);
                if (!album.images) continue;

                const blobs = await Promise.all(
                    album.images.map(async (image) => {
                        const res = await fetch(image, { cache: "no-store" });
                        const blob = res.blob();
                        if (toastId.current) {
                            toast.update(toastId.current, { progress: ++downloaded / totalFiles });
                        }
                        return blob;
                    })
                );

                blobs.forEach((blob, index) => {
                    folder!.file(`${album.albumName}_${index}.jpg`, blob);
                });
            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `${vesselData?.ShipName}_Images.zip`);
            if (toastId.current) toast.dismiss(toastId.current);
        } catch {
            if (toastId.current) toast.dismiss(toastId.current);
            showRequestErrorToast(t("downloads.photosZipFailed"));
        }
    };

    const imageCount = (album: GetPhotosResponse) => getAlbumImages(album).length;

    const itemTemplate = (image: string) => (
        <img src={image} alt="" className="w-full" />
    );

    const thumbnailTemplate = (image: string) => (
        <img src={image} alt="" style={{ width: "80px" }} className="images-thumbnail" />
    );

    const captionTemplate = () => (
        <div className="images-gallery-info">
            {activeIndex + 1} of {selectedAlbum ? getAlbumImages(selectedAlbum).length : 0}
        </div>
    );

    const getAlbumImages = (album: GetPhotosResponse) => {
        const images = [...(album.images ?? [])];
        if (!images.includes(album.frontImage)) {
            images.unshift(album.frontImage);
        }
        return images;
    };

    return (
        <div>
            {!restrictedView && (
                <div className="flex justify-content-end mb-3">
                    <Button
                        label={t("images.downloadAll")}
                        icon="pi pi-download"
                        onClick={handleDownloadAll}
                        className="images-download-btn"
                        severity="secondary"
                    />
                </div>
            )}
            <div className="grid">
                {albums.map((album) => (
                    <div key={album.albumName} className="col-3 images-grid-item">
                        <div
                            className="cursor-pointer"
                            onClick={() => { if (imageCount(album) > 0) { setActiveIndex(0); setSelectedAlbum(album); } }}
                        >
                            {imageCount(album) === 0 ? (
                                <div className="w-full border-round flex align-items-center justify-content-center images-placeholder">
                                    <img src="/no-images.svg" alt="No images" className="images-placeholder-icon" />
                                </div>
                            ) : (
                                <div className="images-thumbnail-wrapper">
                                    <img
                                        src={album.frontImage}
                                        alt={album.albumName}
                                        className="w-full images-thumbnail"
                                    />
                                    <div className="images-thumbnail-overlay">
                                        <i className="pi pi-eye" />
                                    </div>
                                </div>
                            )}
                            <p className="mb-1 mt-2 font-medium">{album.albumName}</p>
                            <div className="text-600 text-sm flex align-items-center mt-1">
                                <i className="pi pi-images mr-1" />
                                <span>{imageCount(album)} {imageCount(album) === 1 ? t("images.image") : t("images.images")}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {selectedAlbum && (
                <div className="images-gallery-overlay" onClick={() => setSelectedAlbum(undefined)}>
                    <Button
                        icon="pi pi-times"
                        className="images-gallery-close"
                        onClick={() => setSelectedAlbum(undefined)}
                        rounded
                        text
                    />
                    <div className="images-gallery-container" onClick={(e) => e.stopPropagation()}>
                        <Galleria
                            value={getAlbumImages(selectedAlbum)}
                            activeIndex={activeIndex}
                            onItemChange={(e) => setActiveIndex(e.index)}
                            item={itemTemplate}
                            thumbnail={thumbnailTemplate}
                            caption={captionTemplate}
                            numVisible={9}
                            showItemNavigators
                            circular
                            style={{ width: "100%" }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
